<i18n src="@/locales/modules/ReportFormTranslate.json"></i18n>

<template>
	<div :class="['report-form-config', { 'report-form-config--create-mode': isCreateMode }]" :key="isCreateMode">
		<div class="header report-form-config__header flex justify-between mb-8 flex-wrap">
			<div class="header__navigation flex items-center">
				<i class="nucleo nucleo-arrow-left text-black text-3xl mr-6 sm:mr-12 cursor-pointer" @click="handleClickBackButton"></i>
				<h2>{{ isCreateMode ? $t("AddNewReportForm") : $tc("ConfigReportForm", localValue.name || "") }}</h2>
			</div>
		</div>

		<form
			@submit.prevent=""
			autocomplete="off"
			class="config-form report-form-config__config-form m-auto max-w-3xl"
		>
			<div class="my-5">
				<legend>{{ $t("ReportFormCode") }}<span class="text-danger">&nbsp;*</span>:</legend>
				<cbs-input
					name="code"
					class="inputx w-full"
					:disabled="!isCreateMode && localValue && !localValue.can_update"
					:placeholder="`${$t('Actions.Input')} ${$t('ReportFormCode').toLowerCase()}`"
					v-model="localValue.code"
					:data-vv-as="$t('ReportFormCode')"
					v-validate="'required'"
				/>
				<span class="text-danger text-sm">{{ errors.first("code") }}</span>
			</div>

			<div class="my-5">
				<legend>{{ $t("ReportFormName") }}<span class="text-danger">&nbsp;*</span>:</legend>
				<cbs-input
					name="name"
					class="inputx w-full"
					:disabled="!isCreateMode && localValue && !localValue.can_update"
					:placeholder="`${$t('Actions.Input')} ${$t('ReportFormName').toLowerCase()}`"
					v-model="localValue.name"
					:data-vv-as="$t('ReportFormName')"
					v-validate="'required'"
				/>
				<span class="text-danger text-sm">{{ errors.first("name") }}</span>
			</div>

			<div class="my-5">
				<legend class>{{ $t("ReportFormType") }}<span class="text-danger">&nbsp;*</span>:</legend>
				<v-select
					name="type"
					label="name"
					v-validate="'required'"
					v-model="localValue.category_id"
					:options="listTypes"
					:reduce="type => type.id"
					:data-vv-as="$t('ReportFormType')"
					:disabled="!isCreateMode && localValue && !localValue.can_update"
					:placeholder="`${$t('Actions.Choose')} ${$t('ReportFormType').toLowerCase()}`"
				>
					<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
				</v-select>
				<span class="text-danger text-sm">{{ errors.first("type") }}</span>
			</div>

			<cbs-dynamic-form-config
				v-if="!isCreateMode"
				class="my-5"
				v-model="reportFormData.controls"
				:title="$t('Form')"
				:isEditable="true"
				:isShowToggleMenu="false"
				:isCanControlField="reportFormData && reportFormData.can_update"
				@onSortField="handleSortField"
				@onClickField="handleClickField"
				@onClickAddField="handleClickAddField"
				@onClickDeleteField="handleClickDeleteField"
			/>

			<div class="flex items-center leading-full">
				<vs-switch color="success" v-model="localValue.isDisabled" :disabled="!isCreateMode && localValue && !localValue.can_update" />
				<span class="ml-3">{{ $t("IsDisabledReport") }}</span>
			</div>

			<div class="config-form__footer text-center mt-3">
				<vs-button
					v-if="isCreateMode || reportFormData && reportFormData.can_update"
					@click.stop="submitData"
					class="w-full sm:w-1/3 font-bold"
				>{{ $t('Actions.Save') }}</vs-button>
			</div>
		</form>

		<config-field-dialog
			v-if="isShowConfigFieldDialog"
			v-model="configFieldDialogData.value"
			:isEditable="true"
			:title="configFieldDialogData.title"
			:formFields="configFieldDialogData.formFields"
			:isDialogActive="isShowConfigFieldDialog"
			@onSubmit="handleCreateOrUpdateField"
			@closeDialog="toggleConfigFieldDialog"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import { showConfirm, detectLeavePage } from "@/mixins/mixins.js";
import moduleReportFormConfig from "@/store/report-form-config/moduleReportFormConfig";

import vSelect from "vue-select";
import CbsDynamicFormConfig from "@/components/cbs-dynamic-form-config/CbsDynamicFormConfig.vue";
import ConfigFieldDialog from "@/components/cbs-dynamic-form-config/ConfigFieldDialog.vue";

const initFieldData = {
	id: null,
	title: "",
	children: [],
	control: null,
	attributes: null,
	enable: true,
	is_optional: false,
	description: {
		value: "",
		enable: false,
		position: null
	}
};

export default {
	name: "ReportFormConfig",
	mixins: [showConfirm, detectLeavePage("isFormDirty")],
	components: { vSelect, CbsDynamicFormConfig, ConfigFieldDialog },
	props: {
		value: {
			type: Object,
			validator: prop => {
				if (prop === undefined) {
					return true;
				}
				return (
					prop.hasOwnProperty("name") &&
					prop.hasOwnProperty("code") &&
					prop.hasOwnProperty("isDisabled") &&
					prop.hasOwnProperty("category_id")
				);
			}
		},
		isCreateMode: Boolean
	},
	emits: {
		onSubmit: {},
		onClickBack: {},
		onIsFormDirtyChange: {}
	},
	data() {
		return {
			initFormData: {},
			localReportFormData: {
				code: "",
				name: "",
				category_id: null,
				isDisabled: false
			},
			configFieldDialogData: null,
			isShowConfigFieldDialog: false
		};
	},
	computed: {
		reportFormData() {
			if(!this.isCreateMode) {
				return this.$store.getters["reportFormConfig/getReportFormData"];
			}
			return {};
		},
		localValue: {
			get() {
				return this.value || this.localReportFormData;
			},
			set(val) {
				if(this.value) {
					this.$emit("input", val);
				}
			}
		},
		listTypes() {
			return [
				{ id: this.$constant.TODO_CATEGORY.NORMAL, name: this.$t("General") },
				{ id: this.$constant.TODO_CATEGORY.DEVICE, name: this.$t("Device") }
			]
		},
		isFormDirty() {
			return JSON.stringify(this.initFormData) !== JSON.stringify(this.localValue);
		}
	},
	watch: {
		isFormDirty() {
			this.$emit("onIsFormDirtyChange", this.isFormDirty);
		}
	},
	methods: {
		async fetchReportFormData() {
			try {
				this.$vs.loading()
				const response = await this.$store.dispatch("reportFormConfig/getReportForm",{id:this.$route.params.id, list_all_controls:true});
				this.localReportFormData = {
					...response,
					isDisabled: !response.enable
				};
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		handleClickBackButton() {
			if (this.isCreateMode) {
				if (this.isFormDirty) {
					this.mixinShowConfirm({
						text: this.$t("Notify.LeaveConfirm")
					}).then(async isAccept => {
						if(isAccept) {
							this.$emit("onClickBack");
						}
					});
				} else {
					this.$emit("onClickBack");
				}
			} else {
				this.$router.go(-1);
			}
		},
		toggleConfigFieldDialog(val = false) {
			this.isShowConfigFieldDialog = val;
		},
		handleClickField(data) {
			this.configFieldDialogData = {
				title: this.$t("UpdateField"),
				value: _.cloneDeep(data),
				formFields: this.reportFormData.controls || []
			};
			this.toggleConfigFieldDialog(true);
		},
		handleClickAddField() {
			
			this.configFieldDialogData = {
				title: this.$t("AddNewField"),
				formFields: this.reportFormData.controls || [],
				value: {
					...initFieldData,
					description: {
						...initFieldData.description,
						position: this.$constant.DYNAMIC_FORM_FIELD_INFO_POSITION.BOTTOM
					}
				}
			};
			this.toggleConfigFieldDialog(true);
		},
		async handleSortField(sortData) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("reportFormConfig/sortReportFormField", sortData);
			} catch (error) {
				this.fetchReportFormData();
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		async handleClickDeleteField(fieldData) {
			try {
				this.$vs.loading();
				await this.$store.dispatch("reportFormConfig/deleteReportFormField", fieldData.id);
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t("DeleteFieldSuccessful")
					}
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		async handleCreateOrUpdateField() {
			try {
				this.$vs.loading();
				const isEdit = !!this.configFieldDialogData.value.id;
				const payload = {
					id: _.get(this.configFieldDialogData.value, "id"),
					title: _.get(this.configFieldDialogData.value, "title"),
					control: _.get(this.configFieldDialogData.value, "control"),
					attributes: _.get(this.configFieldDialogData.value, "attributes"),
					is_optional: _.get(this.configFieldDialogData.value, "is_optional"),
					enable: _.get(this.configFieldDialogData.value, "enable"),
					formable_type: this.$constant.DYNAMIC_FORM_FIELD_MODELS.REPORT_FIELD,
					children: JSON.stringify(_.get(this.configFieldDialogData.value, "children") || []),
					description: _.omitBy(_.get(this.configFieldDialogData.value, "description") || {}, (item, key) => key === "__typename")
				};

				if ([this.$constant.request.control.radio, this.$constant.request.control.checkbox, this.$constant.request.control.dropdown].includes(payload.control)) {
					const payloadAttributes = JSON.parse(payload.attributes);
					payload.attributes = JSON.stringify({
						...payloadAttributes,
						options: payloadAttributes.options.map(option => ({ id: option.id, content: option.content }))
					});
				}

				if(isEdit) {
					await this.$store.dispatch("reportFormConfig/updateReportFormField", payload);
				} else {
					await this.$store.dispatch("reportFormConfig/addReportFormField", {
						...payload,
						formable_id: this.localReportFormData.id
					});
				}

				this.initFormData = _.cloneDeep(this.localValue);
				this.$helper.notifySuccess({
					context: this,
					options: {
						text: this.$t(`${isEdit ? "UpdateFieldSuccessful" : "CreateFieldSuccessful"}`)
					}
				});
				this.toggleConfigFieldDialog(false);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		},
		submitData() {
			this.$validator.validateAll().then(async result => {
				if (result) {
					if(this.isCreateMode) {
						this.$emit("onSubmit", this.localValue);
					} else {
						try {
							this.$vs.loading();

							await this.$store.dispatch("reportFormConfig/updateReportForm", {
								...this.localValue,
								enable: !this.localValue.isDisabled
							});

							this.initFormData = _.cloneDeep(this.localValue);
							this.$helper.notifySuccess({
								context: this,
								options: {
									text: this.$t("UpdateReportFormSuccessful")
								}
							});
						} catch (error) {
							this.$helper.notifyError({ context: this, error });
						} finally {
							this.$nextTick(() => this.$vs.loading.close());
						}
					}
				}
			});
		}
	},
	async created() {
		if (!this.isCreateMode) {
			this.$store.registerModule("reportFormConfig", moduleReportFormConfig);
			await this.fetchReportFormData();
		}
		this.initFormData = _.cloneDeep(this.localValue);
	},
	beforeDestroy() {
		if (!this.isCreateMode) {
			this.$store.commit("reportFormConfig/RESET_STATE");
			this.$store.unregisterModule("reportFormConfig");
		}
	}
};
</script>

<style lang="scss" scoped>
.report-form-config {
	&.report-form-config--create-mode {
		top: -52px;
		position: relative;
		background-color: #fff;
	}
}

.custom-nested-dialog.report-form-field-dialog {
	&:not(:last-child) {
		background: none !important;
	}
}
</style>
